/**
 * Consumo del portfolio mes a mes: la serie y su variación mes contra mes.
 *
 * La pregunta del spec es "¿se usa más o menos?", y el total solo la contesta a
 * medias: lo que se lee acá es la **forma** de la serie. Julio cae contra junio
 * por primera vez en doce meses, y eso es lo que hay que ver de un golpe.
 *
 * Tres decisiones:
 *
 *  - **La variación se deriva en lectura, con `variacionContra` del contrato.**
 *    No se calcula a mano en el JSX: la misma función que usa el servidor para
 *    el KPI héroe arma esta columna, así que la tabla y la tarjeta no pueden
 *    decir porcentajes distintos.
 *  - **Un mes sin medición no es un mes en cero.** El hueco viaja como `null`,
 *    la celda dice "Sin medición" y la variación de ese mes y del siguiente
 *    quedan sin porcentaje: comparar contra un mes que nadie midió inventaría
 *    la mitad del dato.
 *  - **El sparkline no es el dato.** La serie completa va en el nombre accesible
 *    del gráfico, porque una línea de 64px no se lee con un lector de pantalla.
 */
import type { UsagePeriodPoint } from '@ops/contracts'
import {
  formatMetricValue,
  formatPeriodoCorto,
  variacionContra,
} from '@ops/contracts'

import { formatPeriodo } from '../../lib/format'
import {
  DataTable,
  DeltaTag,
  MissingCell,
  Panel,
  Sparkline,
  type Column,
} from '../../ui'
import { Vacio } from '../comunes'
import { baseDeVariacion } from './derivar'

export interface ConsumoMensualProps {
  serie: readonly UsagePeriodPoint[]
  /** El período de la pantalla, para señalar cuál fila es la que se está mirando. */
  periodo: string
  id?: string
}

/** Fila de la tabla: el punto más la variación contra el mes anterior de la serie. */
interface FilaMes {
  punto: UsagePeriodPoint
  anterior: UsagePeriodPoint | null
}

/** La serie dicha en palabras, para quien no ve el gráfico. */
function etiquetaSerie(serie: readonly UsagePeriodPoint[]): string {
  const puntos = serie
    .map(
      (p) =>
        `${formatPeriodoCorto(p.periodo)} ${
          p.creditos === null
            ? 'sin medición'
            : formatMetricValue(p.creditos, 'count')
        }`,
    )
    .join(', ')
  return `Créditos consumidos por el portfolio mes a mes: ${puntos}`
}

export function ConsumoMensual({ serie, periodo, id }: ConsumoMensualProps) {
  if (serie.length === 0) {
    return (
      <div id={id} className="min-w-0">
        <Panel titulo="Consumo mes a mes" padding="sm">
          <Vacio
            icono={null}
            punteada
            mensaje="Todavía no hay ningún mes con consumo medido"
            detalle="La serie la arma el adapter de Langfuse. Sin una corrida no hay meses que mostrar, que no es lo mismo que meses en cero."
          />
        </Panel>
      </div>
    )
  }

  // Del más nuevo al más viejo: la tabla se lee empezando por el mes de la
  // pantalla, no por hace un año.
  const filas: FilaMes[] = serie
    .map((punto, i) => ({ punto, anterior: serie[i - 1] ?? null }))
    .reverse()

  const columnas: ReadonlyArray<Column<FilaMes>> = [
    {
      key: 'mes',
      header: 'Mes',
      rowHeader: true,
      width: '160px',
      sortValue: (f) => f.punto.periodo,
      filterValue: (f) => formatPeriodo(f.punto.periodo),
      render: (f) => (
        <span className="flex items-center gap-2">
          {formatPeriodo(f.punto.periodo)}
          {f.punto.periodo === periodo ? (
            <span className="rotulo text-muted-strong">en pantalla</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'creditos',
      header: 'Créditos',
      align: 'right',
      width: '130px',
      sortValue: (f) => f.punto.creditos,
      render: (f) =>
        f.punto.creditos === null ? (
          <MissingCell que="medición del mes" />
        ) : (
          <span className="tabular">
            {formatMetricValue(f.punto.creditos, 'count')}
          </span>
        ),
    },
    {
      key: 'variacion',
      header: 'Contra el mes anterior',
      width: '230px',
      sortValue: (f) =>
        variacionContra({
          actual: f.punto.creditos,
          anterior: f.anterior?.creditos ?? null,
          periodoBase: f.anterior?.periodo ?? '',
        })?.pct ?? null,
      render: (f) => {
        const variacion =
          f.anterior === null
            ? null
            : variacionContra({
                actual: f.punto.creditos,
                anterior: f.anterior.creditos,
                periodoBase: f.anterior.periodo,
              })
        if (variacion === null) {
          return (
            <MissingCell
              que={
                f.anterior === null
                  ? 'mes anterior en la serie'
                  : 'medición comparable'
              }
            />
          )
        }
        return (
          <DeltaTag
            deltaPct={variacion.pct}
            base={baseDeVariacion(variacion)}
            unidad="count"
            direccion="higher_better"
            size="sm"
          />
        )
      },
    },
    {
      key: 'costo',
      header: 'Costo de servir',
      align: 'right',
      width: '140px',
      sortValue: (f) => f.punto.costoUsd,
      render: (f) =>
        f.punto.costoUsd === null ? (
          <MissingCell que="medición del mes" />
        ) : (
          <span className="tabular">
            {formatMetricValue(f.punto.costoUsd, 'usd')}
          </span>
        ),
    },
    {
      key: 'trazas',
      header: 'Trazas',
      align: 'right',
      width: '120px',
      sortValue: (f) => f.punto.trazas,
      render: (f) =>
        f.punto.trazas === null ? (
          <MissingCell que="medición del mes" />
        ) : (
          <span className="tabular">
            {formatMetricValue(f.punto.trazas, 'count')}
          </span>
        ),
    },
    {
      key: 'tokens',
      header: 'Tokens entrada · salida',
      align: 'right',
      width: '210px',
      sortValue: (f) =>
        f.punto.inputTokens === null && f.punto.outputTokens === null
          ? null
          : (f.punto.inputTokens ?? 0) + (f.punto.outputTokens ?? 0),
      render: (f) =>
        f.punto.inputTokens === null && f.punto.outputTokens === null ? (
          <MissingCell que="medición del mes" />
        ) : (
          <span className="tabular text-micro text-muted-strong">
            {f.punto.inputTokens === null
              ? 'sin dato'
              : formatMetricValue(f.punto.inputTokens, 'count')}{' '}
            ·{' '}
            {f.punto.outputTokens === null
              ? 'sin dato'
              : formatMetricValue(f.punto.outputTokens, 'count')}
          </span>
        ),
    },
  ]

  return (
    <div id={id} className="min-w-0">
      <Panel
        titulo="Consumo mes a mes"
        subtitulo={
          <>
            Créditos consumidos por el portfolio completo, del mes en pantalla
            hacia atrás. Cada variación publica el mes contra el que se compara y
            su valor.
          </>
        }
        padding="none"
        acciones={
          <Sparkline
            valores={serie.map((p) => p.creditos)}
            width={140}
            height={28}
            label={etiquetaSerie(serie)}
          />
        }
        footer={
          <>
            La serie sale de Langfuse agregada por período. Un mes sin medición
            queda vacío y <b>no cuenta como cero</b>: su variación y la del mes
            siguiente se quedan sin porcentaje en vez de inventarlo.
          </>
        }
      >
        <DataTable
          columns={columnas}
          rows={filas}
          rowKey={(f) => f.punto.periodo}
          density="compacta"
          filtroPlaceholder="mes"
          emptyMessage="No hay meses con consumo medido"
          caption="Consumo del portfolio mes a mes: créditos, costo de servir, trazas y tokens, medidos por Langfuse."
        />
      </Panel>
    </div>
  )
}
